/** @format */

import React, { useEffect, useState, useCallback, useMemo } from "react"; // Added useCallback
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
	User,
	KeyRound,
	Loader2,
	AlertCircle,
	LogIn,
	Lock,
} from "lucide-react"; // Refined Icons

// Actions & Components (Ensure paths are correct)
import { login } from "../actions/userActions";
import { FormInput } from "../components/FormInput"; // Assuming path

// --- Reusable Error Message Component ---
const ErrorMessage = ({ message }) => (
	// Slightly softer error styling
	<motion.div
		initial={{ opacity: 0, y: -10 }}
		animate={{ opacity: 1, y: 0 }}
		exit={{ opacity: 0 }}
		role="alert"
		aria-live="assertive" // Announce errors to screen readers
		className="bg-red-50 border border-red-300 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2 mt-1 mb-4" // Added margin
	>
		<AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-500" />
		<p>{message}</p>
	</motion.div>
);

// --- Main Login Screen Component ---
const LoginScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// --- State ---
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false); // Optional: Toggle password visibility

	// --- Redux State ---
	const userLogin = useSelector((state) => state.userLogin);
	const { loading, error, userInfo, isGuest } = userLogin; // Destructure isGuest

	// --- URL Params ---
	let [searchParams] = useSearchParams();
	// Memoize redirect path to prevent effect re-runs if searchParams object changes but value doesn't
	const redirect = useMemo(
		() => searchParams.get("redirect") || "/",
		[searchParams]
	);

	// --- Effects ---
	// Redirect Effect: More concise logic
	useEffect(() => {
		if (userInfo && !isGuest) {
			// Only redirect if logged in (not as guest)
			console.log("User logged in, redirecting to:", redirect);
			navigate(redirect, { replace: true }); // Use replace to avoid adding login to history
		}
	}, [navigate, userInfo, isGuest, redirect]); // Dependencies

	// --- Handlers ---
	const handleSubmit = useCallback(
		(e) => {
			e.preventDefault();
			// Basic client-side check before dispatching
			if (email.trim() && password) {
				dispatch(login(email, password));
			} else {
				// Optionally set a local error state for empty fields
				console.log("Email or password empty");
			}
		},
		[dispatch, email, password]
	); // Dependencies for the callback

	// Determine specific error message for 401
	const specificErrorMessage = useMemo(() => {
		if (!error) return null;
		if (
			error.includes("401") ||
			error.toLowerCase().includes("invalid credentials")
		) {
			return "Incorrect email or password. Please try again.";
		}
		// Generic error for other cases
		return error; // Display the error message from Redux state directly
	}, [error]);

	return (
		<div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.3, ease: "easeOut" }}
				className="max-w-md w-full space-y-8"
			>
				{/* --- Header --- */}
				<div className="text-center">
					{/* Optional: Logo */}
					{/* <img className="mx-auto h-12 w-auto" src="/images/logoDark-1.png" alt="Off Cut Shades"/> */}
					<motion.h2
						initial={{ y: -10 }}
						animate={{ y: 0 }}
						transition={{ delay: 0.1 }}
						className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900"
					>
						Sign in to your account
					</motion.h2>
					<motion.p
						initial={{ y: -10 }}
						animate={{ y: 0 }}
						transition={{ delay: 0.15 }}
						className="mt-2 text-center text-sm text-gray-600"
					>
						Access your orders, saved designs, and faster checkout.
					</motion.p>
				</div>

				{/* --- Form Card --- */}
				<div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
					{/* Display Specific Error Message */}
					<AnimatePresence>
						{specificErrorMessage && (
							<ErrorMessage message={specificErrorMessage} />
						)}
					</AnimatePresence>

					<form onSubmit={handleSubmit} className="space-y-5">
						{/* Use hidden input for accessibility/autocomplete hints */}
						<input
							type="hidden"
							autoComplete="username"
							value={email}
							readOnly
						/>

						<FormInput
							label="Email address"
							type="email"
							name="email" // Add name attribute
							id="email-address" // Add id attribute
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="you@example.com"
							icon={<User />} // Add icon
							autoComplete="email" // Help browser autofill
							disabled={loading}
						/>

						<FormInput
							label="Password"
							type={showPassword ? "text" : "password"} // Toggle type
							name="password" // Add name attribute
							id="password" // Add id attribute
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder="Enter your password"
							icon={<KeyRound />} // Add icon
							autoComplete="current-password" // Help browser autofill
							disabled={loading}
							// Optional: Add visibility toggle button here if needed within FormInput
						/>

						{/* Forgot Password Link */}
						<div className="text-right">
							<Link
								to="/account-recovery" // Ensure this route exists
								className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
							>
								Forgot your password?
							</Link>
						</div>

						{/* Submit Button */}
						<motion.button
							whileTap={{ scale: 0.98 }}
							type="submit"
							disabled={loading}
							className="w-full btn btn-primary py-3 text-base" // Use consistent button style
						>
							{loading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin" /> Signing In...
								</>
							) : (
								<>
									<LogIn className="w-5 h-5" /> Sign In
								</>
							)}
						</motion.button>
					</form>

					{/* --- Separator & Register Link --- */}
					<div className="mt-6 pt-6 border-t border-gray-200">
						<p className="text-center text-sm text-gray-600">
							Don't have an account yet?
						</p>
						<Link
							// Preserve redirect URL for registration page
							to={
								redirect && redirect !== "/"
									? `/register?redirect=${encodeURIComponent(redirect)}`
									: "/register"
							}
							className="mt-3 w-full inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors gap-2"
						>
							Create an Account
						</Link>
						<p className="mt-4 text-xs text-gray-500 text-center flex items-center justify-center gap-1">
							<Lock size={12} /> Your information is secure.
						</p>
					</div>
				</div>
			</motion.div>
		</div>
	);
};

export default LoginScreen;
